// Local storage keys
const PROJECTS_KEY = 'agent_framework_projects';
const VECTOR_STORES_KEY = 'agent_framework_vector_stores';
const AGENTS_KEY = 'agent_framework_agents';
const LAYOUTS_KEY = 'agent_framework_layouts';
const MCP_SERVERS_KEY = 'agent_framework_mcp_servers';

/**
 * Service for handling local storage operations
 */
class StorageService {
  // Project operations
  getProjects() {
    console.log('StorageService: Getting all projects from local storage');
    const projects = window.localStorage.getItem(PROJECTS_KEY);
    return projects ? JSON.parse(projects) : [];
  }

  getProject(projectId) {
    console.log(`StorageService: Getting project ${projectId} from local storage`);
    const projects = this.getProjects();
    return projects.find(project => project.id === projectId);
  }

  saveProject(project) {
    console.log(`StorageService: Saving project with ID ${project.id}`);
    const projects = this.getProjects();
    const existingIndex = projects.findIndex(p => p.id === project.id);
    
    if (existingIndex >= 0) {
      projects[existingIndex] = project;
    } else {
      projects.push(project);
    }
    
    window.localStorage.setItem(PROJECTS_KEY, JSON.stringify(projects));
    console.log('StorageService: Project saved successfully');
    return project;
  }

  deleteProject(projectId) {
    console.log(`StorageService: Deleting project ${projectId}`);
    const projects = this.getProjects().filter(p => p.id !== projectId);
    window.localStorage.setItem(PROJECTS_KEY, JSON.stringify(projects));
    
    // Also clean up related data
    this.deleteVectorStoresByProject(projectId);
    this.deleteAgentsByProject(projectId);
    this.deleteLayoutsByProject(projectId);
    
    console.log(`StorageService: Project ${projectId} and all related data deleted`);
    return true;
  }

  // Vector store operations
  getVectorStores(projectId) {
    console.log(`StorageService: Getting vector stores for project ${projectId}`);
    const stores = window.localStorage.getItem(VECTOR_STORES_KEY);
    const allStores = stores ? JSON.parse(stores) : [];
    return allStores.filter(store => store.projectId === projectId);
  }
  
  getVectorStore(id) {
    console.log(`StorageService: Getting vector store with ID ${id}`);
    const stores = window.localStorage.getItem(VECTOR_STORES_KEY);
    const allStores = stores ? JSON.parse(stores) : [];
    return allStores.find(store => store.id === id);
  }

  saveVectorStore(vectorStore) {
    console.log(`StorageService: Saving vector store ${vectorStore.id} for project ${vectorStore.projectId}`);
    const stores = window.localStorage.getItem(VECTOR_STORES_KEY);
    const allStores = stores ? JSON.parse(stores) : [];
    
    const existingIndex = allStores.findIndex(s => s.id === vectorStore.id);
    if (existingIndex >= 0) {
      allStores[existingIndex] = vectorStore;
    } else {
      allStores.push(vectorStore);
    }
    
    window.localStorage.setItem(VECTOR_STORES_KEY, JSON.stringify(allStores));
    console.log(`StorageService: Vector store ${vectorStore.id} saved successfully`);
    return vectorStore;
  }

  deleteVectorStore(vectorStoreId) {
    console.log(`StorageService: Deleting vector store ${vectorStoreId}`);
    const stores = window.localStorage.getItem(VECTOR_STORES_KEY);
    const allStores = stores ? JSON.parse(stores) : [];
    const filteredStores = allStores.filter(store => store.id !== vectorStoreId);
    window.localStorage.setItem(VECTOR_STORES_KEY, JSON.stringify(filteredStores));
    console.log(`StorageService: Vector store ${vectorStoreId} deleted`);
    return true;
  }

  deleteVectorStoresByProject(projectId) {
    console.log(`StorageService: Deleting all vector stores for project ${projectId}`);
    const stores = window.localStorage.getItem(VECTOR_STORES_KEY);
    const allStores = stores ? JSON.parse(stores) : [];
    const filteredStores = allStores.filter(store => store.projectId !== projectId);
    window.localStorage.setItem(VECTOR_STORES_KEY, JSON.stringify(filteredStores));
    console.log(`StorageService: All vector stores for project ${projectId} deleted`);
    return true;
  }

  // Agent operations
  getAgents(projectId) {
    console.log(`StorageService: Getting agents for project ${projectId}`);
    const agents = window.localStorage.getItem(AGENTS_KEY);
    const allAgents = agents ? JSON.parse(agents) : [];
    return allAgents.filter(agent => agent.projectId === projectId);
  }

  getAgent(agentId) {
    console.log(`StorageService: Getting agent ${agentId}`);
    const agents = window.localStorage.getItem(AGENTS_KEY);
    const allAgents = agents ? JSON.parse(agents) : [];
    return allAgents.find(agent => agent.id === agentId);
  }

  saveAgent(agent) {
    console.log(`StorageService: Saving agent ${agent.id} for project ${agent.projectId}`);
    const agents = window.localStorage.getItem(AGENTS_KEY);
    const allAgents = agents ? JSON.parse(agents) : [];
    
    const existingIndex = allAgents.findIndex(a => a.id === agent.id);
    if (existingIndex >= 0) {
      allAgents[existingIndex] = agent;
    } else {
      allAgents.push(agent);
    }
    
    window.localStorage.setItem(AGENTS_KEY, JSON.stringify(allAgents));
    console.log(`StorageService: Agent ${agent.id} saved successfully`);
    return agent;
  }

  deleteAgent(agentId) {
    console.log(`StorageService: Deleting agent ${agentId}`);
    const agents = window.localStorage.getItem(AGENTS_KEY);
    const allAgents = agents ? JSON.parse(agents) : [];
    const filteredAgents = allAgents.filter(agent => agent.id !== agentId);
    window.localStorage.setItem(AGENTS_KEY, JSON.stringify(filteredAgents));
    console.log(`StorageService: Agent ${agentId} deleted`);
    return true;
  }

  deleteAgentsByProject(projectId) {
    console.log(`StorageService: Deleting all agents for project ${projectId}`);
    const agents = window.localStorage.getItem(AGENTS_KEY);
    const allAgents = agents ? JSON.parse(agents) : [];
    const filteredAgents = allAgents.filter(agent => agent.projectId !== projectId);
    window.localStorage.setItem(AGENTS_KEY, JSON.stringify(filteredAgents));
    console.log(`StorageService: All agents for project ${projectId} deleted`);
    return true;
  }

  // Layout operations for the agent map
  getLayout(projectId) {
    console.log(`StorageService: Getting agent map layout for project ${projectId}`);
    const layouts = window.localStorage.getItem(LAYOUTS_KEY);
    const allLayouts = layouts ? JSON.parse(layouts) : [];
    return allLayouts.find(layout => layout.projectId === projectId) || { 
      projectId, 
      agents: {}, 
      edges: [] 
    };
  }

  saveLayout(layout) {
    console.log(`StorageService: Saving agent map layout for project ${layout.projectId}`);
    const layouts = window.localStorage.getItem(LAYOUTS_KEY);
    const allLayouts = layouts ? JSON.parse(layouts) : [];
    
    const existingIndex = allLayouts.findIndex(l => l.projectId === layout.projectId);
    if (existingIndex >= 0) {
      allLayouts[existingIndex] = layout;
    } else {
      allLayouts.push(layout);
    }
    
    window.localStorage.setItem(LAYOUTS_KEY, JSON.stringify(allLayouts));
    console.log(`StorageService: Layout for project ${layout.projectId} saved successfully`);
    return layout;
  }

  deleteLayoutsByProject(projectId) {
    console.log(`StorageService: Deleting agent map layout for project ${projectId}`);
    const layouts = window.localStorage.getItem(LAYOUTS_KEY);
    const allLayouts = layouts ? JSON.parse(layouts) : [];
    const filteredLayouts = allLayouts.filter(layout => layout.projectId !== projectId);
    window.localStorage.setItem(LAYOUTS_KEY, JSON.stringify(filteredLayouts));
    console.log(`StorageService: Layout for project ${projectId} deleted`);
    return true;
  }
  
  // MCP Server operations
  getMCPServers() {
    console.log('StorageService: Getting all MCP servers from local storage');
    const servers = window.localStorage.getItem(MCP_SERVERS_KEY);
    return servers ? JSON.parse(servers) : [];
  }

  getMCPServer(serverId) {
    console.log(`StorageService: Getting MCP server ${serverId} from local storage`);
    const servers = this.getMCPServers();
    return servers.find(server => server.id === serverId);
  }

  saveMCPServer(server) {
    console.log(`StorageService: Saving MCP server with ID ${server.id}`);
    const servers = this.getMCPServers();
    const existingIndex = servers.findIndex(s => s.id === server.id);
    
    if (existingIndex >= 0) {
      servers[existingIndex] = server;
    } else {
      servers.push(server);
    }
    
    window.localStorage.setItem(MCP_SERVERS_KEY, JSON.stringify(servers));
    console.log('StorageService: MCP server saved successfully');
    return server;
  }

  deleteMCPServer(serverId) {
    console.log(`StorageService: Deleting MCP server ${serverId}`);
    const servers = this.getMCPServers().filter(s => s.id !== serverId);
    window.localStorage.setItem(MCP_SERVERS_KEY, JSON.stringify(servers));
    console.log(`StorageService: MCP server ${serverId} deleted`);
    return true;
  }

  // Get MCP servers for a project
  getProjectMCPServers(projectId) {
    console.log(`StorageService: Getting MCP servers for project ${projectId}`);
    const servers = this.getMCPServers();
    return servers.filter(server => server.projectIds && server.projectIds.includes(projectId));
  }

  // Link an MCP server to a project
  linkMCPServerToProject(serverId, projectId) {
    console.log(`StorageService: Linking MCP server ${serverId} to project ${projectId}`);
    const server = this.getMCPServer(serverId);
    
    if (!server) {
      console.error(`StorageService: MCP server ${serverId} not found`);
      return false;
    }
    
    // Create or update projectIds array
    if (!server.projectIds) {
      server.projectIds = [];
    }
    
    // Add projectId if not already linked
    if (!server.projectIds.includes(projectId)) {
      server.projectIds.push(projectId);
      this.saveMCPServer(server);
      console.log(`StorageService: MCP server ${serverId} linked to project ${projectId}`);
    } else {
      console.log(`StorageService: MCP server ${serverId} already linked to project ${projectId}`);
    }
    
    return true;
  }

  // Unlink an MCP server from a project
  unlinkMCPServerFromProject(serverId, projectId) {
    console.log(`StorageService: Unlinking MCP server ${serverId} from project ${projectId}`);
    const server = this.getMCPServer(serverId);
    
    if (!server || !server.projectIds) {
      console.error(`StorageService: MCP server ${serverId} not found or has no project links`);
      return false;
    }
    
    // Remove projectId if linked
    const index = server.projectIds.indexOf(projectId);
    if (index !== -1) {
      server.projectIds.splice(index, 1);
      this.saveMCPServer(server);
      console.log(`StorageService: MCP server ${serverId} unlinked from project ${projectId}`);
    } else {
      console.log(`StorageService: MCP server ${serverId} was not linked to project ${projectId}`);
    }
    
    return true;
  }
}

export default new StorageService();